import { useEffect, useState } from 'react';

import {
  downloadSentimentBranchPdf,
  downloadSentimentBranchReport,
  downloadSentimentEscalationPdf,
  downloadSentimentEscalationReport,
  downloadSentimentEvolutionPdf,
  downloadSentimentEvolutionReport,
  downloadSentimentTagPdf,
  downloadSentimentTagReport,
  downloadSentimentTrendCsv,
  downloadSentimentTrendPdf,
  downloadTrafficReportCsv,
  downloadTrafficReportPdf,
  getSentimentReportConfig,
} from '../api/client.js';
import ReportCard from '../components/ReportCard.jsx';
import SentimentLayout from '../components/SentimentLayout.jsx';

function presetRange(preset) {
  const iso = (d) => d.toISOString().slice(0, 10);
  const today = new Date();
  const end = iso(today);
  if (preset === 'today') return { start: end, end };
  if (preset === 'week') {
    const d = new Date(today);
    d.setDate(d.getDate() - 6);
    return { start: iso(d), end };
  }
  if (preset === 'month') {
    const d = new Date(today);
    d.setDate(d.getDate() - 29);
    return { start: iso(d), end };
  }
  return { start: '', end: '' }; // all time
}

// Trend-report presets are wider windows (30/60/90 days).
function presetDays(n) {
  const iso = (d) => d.toISOString().slice(0, 10);
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (n - 1));
  return { start: iso(start), end: iso(end) };
}

function DateRange({ range, onChange }) {
  const set = (patch) => onChange({ ...range, ...patch });
  return (
    <div className="report-card__range">
      <label className="report-card__field">
        <span>Start</span>
        <input type="date" value={range.start} onChange={(e) => set({ start: e.target.value })} />
      </label>
      <label className="report-card__field">
        <span>End</span>
        <input type="date" value={range.end} onChange={(e) => set({ end: e.target.value })} />
      </label>
      <div className="report-card__presets">
        <button type="button" className="report-card__preset" onClick={() => onChange(presetRange('today'))}>Today</button>
        <button type="button" className="report-card__preset" onClick={() => onChange(presetRange('week'))}>Last 7 Days</button>
        <button type="button" className="report-card__preset" onClick={() => onChange(presetRange('month'))}>Last 30 Days</button>
        <button type="button" className="report-card__preset" onClick={() => onChange(presetRange('all'))}>All Time</button>
      </div>
    </div>
  );
}

function TrendControls({ range, onChange, granularity, onGranularity }) {
  const set = (patch) => onChange({ ...range, ...patch });
  return (
    <div className="report-card__range">
      <label className="report-card__field">
        <span>Start</span>
        <input type="date" value={range.start} onChange={(e) => set({ start: e.target.value })} />
      </label>
      <label className="report-card__field">
        <span>End</span>
        <input type="date" value={range.end} onChange={(e) => set({ end: e.target.value })} />
      </label>
      <div className="report-card__presets">
        <button type="button" className="report-card__preset" onClick={() => onChange(presetDays(30))}>Last 30 Days</button>
        <button type="button" className="report-card__preset" onClick={() => onChange(presetDays(60))}>Last 60 Days</button>
        <button type="button" className="report-card__preset" onClick={() => onChange(presetDays(90))}>Last 90 Days</button>
        <button type="button" className="report-card__preset" onClick={() => onChange({ start: '', end: '' })}>All Time</button>
      </div>
      <label className="report-card__field">
        <span>Granularity</span>
        <select value={granularity} onChange={(e) => onGranularity(e.target.value)}>
          <option value="week">Weekly</option>
          <option value="day">Daily</option>
        </select>
      </label>
    </div>
  );
}

function rangeParams(range) {
  const p = {};
  if (range.start) p.start_date = range.start;
  if (range.end) p.end_date = range.end;
  return p;
}

export default function SentimentReportsPage() {
  const [trendRange, setTrendRange] = useState(presetDays(90));
  const [trendGran, setTrendGran] = useState('week');
  const [migrationDate, setMigrationDate] = useState(null);
  const [tagRange, setTagRange] = useState({ start: '', end: '' });
  const [escRange, setEscRange] = useState({ start: '', end: '' });
  const [escType, setEscType] = useState('all');
  const [branchRange, setBranchRange] = useState({ start: '', end: '' });
  // Defaults to last 7 days so the PDF's week-over-week comparison is meaningful.
  const [evoRange, setEvoRange] = useState(presetRange('week'));
  // Defaults to today — this one is pulled daily during the upgrade window.
  const [trafficRange, setTrafficRange] = useState(presetRange('today'));

  useEffect(() => {
    getSentimentReportConfig()
      .then((c) => setMigrationDate(c?.migration_go_live_date || null))
      .catch(() => {});
  }, []);

  const trendParams = () => ({ ...rangeParams(trendRange), granularity: trendGran });

  return (
    <SentimentLayout title="Sentiment Reports">
      <div className="reports-grid">
        <ReportCard
          title="Sentiment Over Time"
          description="Track how customer sentiment has changed over time. See the impact of the Evolution migration with the go-live date marked on the chart."
          buttonLabel="Download PDF"
          onGenerate={() => downloadSentimentTrendPdf(trendParams())}
          secondaryLabel="Download CSV"
          onSecondary={() => downloadSentimentTrendCsv(trendParams())}
        >
          <TrendControls
            range={trendRange}
            onChange={setTrendRange}
            granularity={trendGran}
            onGranularity={setTrendGran}
          />
          {migrationDate && (
            <div className="report-card__note">
              Evolution go-live date: {migrationDate} will be marked on the PDF
            </div>
          )}
        </ReportCard>

        <ReportCard
          title="Upgrade Web Traffic"
          description="Live Google Analytics pull: content engagement by page, how visitors reach the upgrade page (including which homepage links they click), what they click once there, and contact page activity with form submissions."
          buttonLabel="Download PDF"
          onGenerate={() => downloadTrafficReportPdf(rangeParams(trafficRange))}
          secondaryLabel="Download CSV"
          onSecondary={() => downloadTrafficReportCsv(rangeParams(trafficRange))}
        >
          <DateRange range={trafficRange} onChange={setTrafficRange} />
        </ReportCard>

        <ReportCard
          title="Tag & Theme Frequency"
          description="Which topics are appearing most and how sentiment breaks down per theme. Use date range to compare periods."
          buttonLabel="Download PDF"
          onGenerate={() => downloadSentimentTagPdf(rangeParams(tagRange))}
          secondaryLabel="Download CSV"
          onSecondary={() => downloadSentimentTagReport(rangeParams(tagRange))}
        >
          <DateRange range={tagRange} onChange={setTagRange} />
        </ReportCard>

        <ReportCard
          title="Evolution-Related Mentions"
          description="Reviews and mentions the AI flagged as related to the Evolution migration. PDF adds a week-over-week trend, sentiment/source/confidence breakdowns, the tags that came with them, and a pre/post go-live comparison. CSV is the full drill-down list."
          buttonLabel="Download PDF"
          onGenerate={() => downloadSentimentEvolutionPdf(rangeParams(evoRange))}
          secondaryLabel="Download CSV"
          onSecondary={() => downloadSentimentEvolutionReport(rangeParams(evoRange))}
        >
          <DateRange range={evoRange} onChange={setEvoRange} />
          {migrationDate && (
            <div className="report-card__note">
              Evolution go-live date: {migrationDate} — pre/post comparison shown on the PDF
            </div>
          )}
        </ReportCard>

        <ReportCard
          title="Escalation Report"
          description="All mentions flagged for Command Center, Compliance, or Client Solution Center. Use to brief leadership or track response."
          buttonLabel="Download PDF"
          onGenerate={() =>
            downloadSentimentEscalationPdf({
              ...rangeParams(escRange),
              ...(escType !== 'all' ? { escalation_type: escType } : {}),
            })
          }
          secondaryLabel="Download CSV"
          onSecondary={() =>
            downloadSentimentEscalationReport({
              ...rangeParams(escRange),
              ...(escType !== 'all' ? { escalation_type: escType } : {}),
            })
          }
        >
          <label className="report-card__field">
            <span>Escalation Type</span>
            <select value={escType} onChange={(e) => setEscType(e.target.value)}>
              <option value="all">All Escalations</option>
              <option value="command_center">Command Center</option>
              <option value="compliance">Compliance</option>
              <option value="client_solution_center">Client Solution Center</option>
            </select>
          </label>
          <DateRange range={escRange} onChange={setEscRange} />
        </ReportCard>

        <ReportCard
          title="Branch Sentiment"
          description="Google review sentiment and ratings broken down by Centier branch location. Identifies branches with elevated customer friction."
          buttonLabel="Download PDF"
          onGenerate={() => downloadSentimentBranchPdf(rangeParams(branchRange))}
          secondaryLabel="Download CSV"
          onSecondary={() => downloadSentimentBranchReport(rangeParams(branchRange))}
        >
          <DateRange range={branchRange} onChange={setBranchRange} />
        </ReportCard>
      </div>
    </SentimentLayout>
  );
}
